/* global MutationObserver */
import {useCallback, useEffect, useRef, useState} from 'react';
import Prism from 'prismjs';
import 'prismjs/components/prism-diff';
import 'prismjs/plugins/diff-highlight/prism-diff-highlight';
import 'prismjs/plugins/filter-highlight-all/prism-filter-highlight-all';
import 'prismjs/plugins/line-highlight/prism-line-highlight';
import scratchblocks from 'scratchblocks';
import c from '../../utils/c';
// eslint-disable-next-line import/no-cycle
import SlideViewerQuestion from '../styled-slideviewer/partials/questions/SlideViewerQuestion';
import {ScrollIndicator} from './ScrollIndicator';
import {elementHasNoTextContent} from './elementHasNoTextContent';
import useFormatMessage from '../../hooks/useFormatMessage';
import {linkFocusStyles} from '../../utils/constants/linkFocusStyles';

const slideWidth = 1366;
const slideHeight = 768;

export function SlideViewerCk5Slide({
  children,
  currentSlide,
  hasFixedDimensions,
  id = '',
  isClickable = true,
  isFullSlideViewer = true,
  isThumbnail = false,
  isTransparent,
  shouldCenter = true,
  showAnswers = false,
}) {
  const t = useFormatMessage();
  const contentElementRef = useRef();
  const [scale, setScale] = useState(1);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [question] = currentSlide?.questions || [];

  const calculateScale = useCallback(() => {
    const container = isThumbnail
      ? document.getElementsByClassName('slideThumbnail')[0]
      : document.getElementById(`slideViewerContentContainer${id}`);

    if (!container) return;

    const { clientHeight: containerHeight, clientWidth: containerWidth } =
      container;

    const scaleValue = Math.min(
      containerWidth / slideWidth,
      containerHeight / slideHeight,
    );

    setScale(scaleValue);

    if (isThumbnail) return;

    if (shouldCenter) {
      setTop((containerHeight - slideHeight * scaleValue) / 2);
      setLeft((containerWidth - slideWidth * scaleValue) / 2);
    }
  }, [id, isThumbnail, shouldCenter]);

  useEffect(() => {
    if (hasFixedDimensions) {
      calculateScale();
      addEventListener('resize', calculateScale);
    }
  }, [calculateScale, hasFixedDimensions]);

  useEffect(() => {
    if (isFullSlideViewer || isThumbnail) {
      // Scratch block generation
      const elements = document.querySelectorAll(
        'code.language-blocks:not(.styled)',
      );
      const options = {
        inline: false,
        languages: ['en'],
        scale: 1,
        style: 'scratch3',
      };
      if (elements.length > 0) {
        elements.forEach((el) => {
          el.classList.add('styled');
          const code = scratchblocks.read(el);
          const doc = scratchblocks.parse(code, options);
          const svg = scratchblocks.render(doc, options);
          scratchblocks.replace(el, svg, doc, options);
        });
      }

      // Modify the Prism highlightAll function to exclude code blocks that have already been highlighted and
      // codeblocks that have language "blocks".
      // If we don't filter out the code blocks that have already been highlighted, then the spans from the
      // highlighting will be removed by Prism & we get just plain text without any styling.
      Prism.plugins.filterHighlightAll.add((env) => {
        const languageIsNotBlocks = env.language !== 'blocks';
        const codeIsNotStyledAlready = !env.element.querySelector('.token');

        return languageIsNotBlocks && codeIsNotStyledAlready;
      });
      Prism.highlightAll();
    }
  }, [children, isFullSlideViewer, isThumbnail]);

  useEffect(() => {
    /**
     * the below code serves to ensure that the scroll-indicator isn't shown if
     * the content editors had fun adding empty p-tags with just a nbsp in them
     * at the bottom.
     */
    if (isFullSlideViewer) {
      let lastElementIsNotEmpty = true;

      while (lastElementIsNotEmpty) {
        const lastParagraph = contentElementRef.current.querySelector(
          '.ck5Slide > p:last-child, .ck5Slide > *:last-child > p:last-child',
        );
        const lastParagraphHasNoChildren =
          lastParagraph?.childElementCount === 0;

        if (
          lastParagraph &&
          lastParagraphHasNoChildren &&
          elementHasNoTextContent(lastParagraph)
        ) {
          lastParagraph.remove();
        } else {
          lastElementIsNotEmpty = false;
        }
      }
    }
  }, [children, isFullSlideViewer]);

  useEffect(() => {
    const addCopyButtons = (target = document) => {
      // Only process code blocks within our slide content
      const codeBlocks = target.querySelectorAll(
        'pre code:not([data-copy-processed])',
      );

      codeBlocks.forEach((codeBlock) => {
        // Mark as processed to avoid re-processing
        codeBlock.setAttribute('data-copy-processed', 'true');

        // Skip if there already is a copy button
        if (codeBlock.parentElement.querySelector('.copy-button')) {
          return;
        }

        // Create button
        const button = document.createElement('button');
        button.type = 'button'; // Prevent form submission
        button.className =
          `copy-button absolute top-2 right-2 px-2 py-1 text-xs bg-accent-600 hover:underline` +
          ` hover:underline-offset-4 rounded ${linkFocusStyles}`;
        button.textContent = t('global.copy');
        button.setAttribute('aria-label', t('global.copy'));

        // Position the pre element relatively & add the button
        const pre = codeBlock.parentElement;
        if (pre) {
          pre.style.position = 'relative';
          pre.prepend(button);
        }

        button.addEventListener('click', async (e) => {
          e.stopPropagation();
          try {
            // Create a clone of the code block to manipulate
            const codeClone = codeBlock.cloneNode(true);
            // Remove indicators that a line was added or deleted (from highlighting)
            const diffIndicators = codeClone.querySelectorAll(
              'strong.token.prefix.inserted, strong.token.prefix.deleted',
            );
            diffIndicators.forEach((indicator) => indicator.remove());
            // copy the textContent
            await navigator.clipboard.writeText(codeClone.textContent || '');

            // give feedback to the user
            const originalText = button.textContent;
            button.textContent = t('global.copied');
            button.classList.add('bg-green-600');
            setTimeout(() => {
              button.textContent = originalText;
              button.classList.remove('bg-green-600');
            }, 2000);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to copy text: ', err);
            button.textContent = t('global.failed');
          }
        });
      });
    };

    // Run once on mount, targeting only the slide content
    if (contentElementRef.current) {
      addCopyButtons(contentElementRef.current);
    }

    // Handle dynamically added code blocks (if any)
    // Only observe the slide content, not the entire document
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Only process the nodes that were added
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Only process element nodes
              addCopyButtons(node);
            }
          });
        }
      }
    });

    // Only observe the slide content, not the entire document
    if (contentElementRef.current) {
      observer.observe(contentElementRef.current, {
        attributes: false,
        characterData: false,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        className={c(
          'slideviewerCK5Overlay rounded origin-top-left flex flex-col',
          hasFixedDimensions && 'w-[1366px] h-[768px] absolute',
          isClickable || 'cursor-not-allowed z-10',
        )}
        style={{ left: `${left}px`, scale: String(scale), top: `${top}px` }}
      />
      <div
        className={c(
          'slideviewerCK5Slide rounded origin-top-left flex flex-col',
          hasFixedDimensions && 'w-[1366px] h-[768px] absolute',
          hasFixedDimensions && !isTransparent && !isThumbnail && 'bg-white',
        )}
        style={{ left: `${left}px`, scale: String(scale), top: `${top}px` }}
      >
        <div
          key={`slide-${currentSlide?.id}`}
          ref={contentElementRef}
          className={c(
            'ck5Slide rounded flex-1 overflow-x-hidden',
            isClickable ? 'overflow-y-auto' : 'overflow-y-hidden',
          )}
        >
          {children}
        </div>
        {question && hasFixedDimensions && (
          <div className="flex-none text-black px-12 py-4 rounded bg-white relative max-h-[75%]">
            <ScrollIndicator contentElementRef={contentElementRef} />
            <SlideViewerQuestion
              key={`question-${currentSlide.id}`}
              canSubmit
              question={question}
              showAnswers={showAnswers}
            />
          </div>
        )}
      </div>
    </>
  );
}
